<!-- LoopScene.vue - "share -> AI -> better infrastructure" loop (was public/landing_content_loop.svg:
     English text as outlines, emoji as PNG). Labels from i18n, colors from --ill-* tokens, bulb as vector.
     Beats: your report travels into the AI, the model chips light up, the idea travels back and the
     bulb switches on. CSS by default; LoopScene.timeline.js is the GSAP version. -->
<template>
  <svg ref="root" class="scene loop-scene" :class="{ play, gsap: gsapOn }" viewBox="0 0 572 224" role="img" :aria-label="$t('landing.scene.loopAlt')">
    <!-- loop bracket: from the bulb back up to "share" -->
    <path data-anim="bracket" class="s-ink" d="M118 84V58c0-7 5-12 12-12h4" stroke-width="2" stroke-linecap="round" />
    <path class="s-ink" d="M131 41l5 5-5 5" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" />

    <text data-anim="share" class="label" x="142" y="51">{{ $t('landing.scene.loopShare') }}</text>

    <!-- to the AI -->
    <path data-anim="trail-in" class="trail s-ink" d="M318 46H350" />
    <path class="s-ink" d="M347 41l5 5-5 5" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" />
    <circle data-anim="dot-in" class="dot dot-in f-mint" cx="318" cy="46" r="5" />

    <!-- AI hub -->
    <g data-anim="hub" class="hub">
      <circle class="f-mint" cx="400.18" cy="71.92" r="43.02" />
      <text class="hub-label" x="400" y="83" text-anchor="middle">{{ $t('landing.scene.ai') }}</text>
    </g>
    <g v-for="(c, i) in chips" :key="c" :data-anim="'chip-' + i" class="chip" :class="'c' + i">
      <rect :x="chipPos[i][0]" :y="chipPos[i][1]" width="51.63" height="29.04" rx="7.04" class="f-chip" />
      <text class="chip-label" :x="chipPos[i][0] + 25.8" :y="chipPos[i][1] + 19.6" text-anchor="middle">{{ c }}</text>
    </g>

    <!-- back to the region -->
    <path data-anim="trail-out" class="trail s-ink" d="M356 97H343" />
    <path class="s-ink" d="M342 92l-5 5 5 5" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" />
    <circle data-anim="dot-out" class="dot dot-out f-spark" cx="356" cy="97" r="5" />

    <text data-anim="improve" class="label" x="146" y="95">
      <tspan x="146">{{ $t('landing.scene.loopImprove1') }}</tspan>
      <tspan x="146" dy="18">{{ $t('landing.scene.loopImprove2') }}</tspan>
    </text>

    <g data-anim="bulb" class="bulb">
      <circle data-anim="glow" class="glow f-spark" cx="129" cy="95" r="19" />
      <circle class="glass" cx="129" cy="95" r="10.5" />
      <path class="s-ink" d="M125 97l4-4 4 4" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round" />
      <rect x="124" y="105" width="10" height="7" rx="2" class="f-steel" />
      <path class="s-paper" d="M124.5 108.5h9" stroke-width="1.2" />
    </g>

    <path data-anim="trail-down" class="trail s-ink" d="M286 215V180" />
  </svg>
</template>

<script>
import { useScene } from '@/anim/useScene';
import timeline from './LoopScene.timeline';

export default {
  name: 'LoopScene',
  setup() {
    return {
      ...useScene({ timeline }),
      chips: ['GPT', 'CR', 'ML'],
      chipPos: [[410.94, 19.21], [421.69, 56.86], [410.94, 94.5]],
    };
  },
};
</script>

<style scoped>
.scene { display: block; width: 100%; height: auto; overflow: visible; }
.scene * { transform-box: view-box; }
.f-mint { fill: var(--ill-mint); } .f-chip { fill: var(--ill-chip); } .f-steel { fill: var(--ill-steel); } .f-spark { fill: var(--ill-spark); }
.s-ink { stroke: var(--ill-ink); fill: none; } .s-paper { stroke: var(--ill-paper); fill: none; }
.trail { stroke-width: 1.5; stroke-dasharray: 6 6; }
.label { font: 400 13.5px Poppins, sans-serif; fill: var(--ill-ink); }
.hub-label { font: 500 31px Poppins, sans-serif; fill: #fff; letter-spacing: 1px; }
.chip-label { font: 500 12px Poppins, sans-serif; fill: #fff; }
.glass { fill: var(--ill-spark); stroke: var(--ill-ink); stroke-width: 1.6; }
.glow { opacity: 0.28; }
.dot { opacity: 0; }

/* CSS timeline (default). Same beats as LoopScene.timeline.js. */
.hub { transform-origin: 400px 72px; }
.chip { transform-box: fill-box; transform-origin: 0% 50%; }
.glow { transform-box: fill-box; transform-origin: center; }
.play .trail { animation: ls-flow 1.8s linear both; }
.play .dot-in { animation: ls-in 0.8s ease-in 0.3s both; }
.play .hub { animation: ls-pulse 0.5s ease-out 1.1s both; }
.play .c0 { animation: ls-chip 0.45s cubic-bezier(0.3, 1.6, 0.5, 1) 1.2s both; }
.play .c1 { animation: ls-chip 0.45s cubic-bezier(0.3, 1.6, 0.5, 1) 1.32s both; }
.play .c2 { animation: ls-chip 0.45s cubic-bezier(0.3, 1.6, 0.5, 1) 1.44s both; }
.play .dot-out { animation: ls-out 0.5s ease-out 1.75s both; }
.play .glass { animation: ls-glass 0.4s ease-out 2.2s both; }
.play .glow { animation: ls-glow 0.9s ease-out 2.2s both; }
@keyframes ls-flow { from { stroke-dashoffset: 24; } to { stroke-dashoffset: 0; } }
@keyframes ls-in { 0% { opacity: 0; transform: none; } 15% { opacity: 1; } 85% { opacity: 1; } 100% { opacity: 0; transform: translateX(38px); } }
@keyframes ls-out { 0% { opacity: 0; transform: none; } 20% { opacity: 1; } 85% { opacity: 1; } 100% { opacity: 0; transform: translateX(-20px); } }
@keyframes ls-pulse { 0%, 100% { transform: scale(1); } 45% { transform: scale(1.08); } }
@keyframes ls-chip { from { opacity: 0; transform: translateX(-14px) scale(0.6); } to { opacity: 1; transform: none; } }
@keyframes ls-glass { from { fill: var(--ill-surface); } to { fill: var(--ill-spark); } }
@keyframes ls-glow { 0% { opacity: 0; transform: scale(0.4); } 60% { opacity: 0.5; transform: scale(1.15); } 100% { opacity: 0.28; transform: scale(1); } }
.gsap * { animation: none !important; }
@media (prefers-reduced-motion: reduce) { .play * { animation: none !important; } }
</style>
